import React, {Component} from 'react';
import {isAndroid} from "react-device-detect";
import {Subject, Subscription} from "rxjs";
import AppService from "./services/AppService";
import AppEntity from "./types/AppEntity";
import User from "./types/User"
import PlatformType from "./types/PlatformType";
import {FormType} from "./types/FormType";
import {TokenState} from "./services/TokenState";
import RolesService from "./services/RolesService";
import ReleasesService from "./services/ReleasesService";
import {errorNotification, successNotification} from "./services/NotificationService";
import {filterStoreMainApp, temporarySubscription} from "./services/Helpers";
import {devicePlatform} from "./services/Platform";
import ApplicationFormDialog from "./components/application-form-dialog/ApplicationFormDialog";
import LoginScreen from "./components/login/LoginScreen";
import TeamPage from "./components/users/TeamPage";
import TopBar, {View} from "./components/apps/TopBar";
import AppList from "./components/apps/AppList";
import {AppAdvertising} from "./components/mobile-advertising/AppAdvertising";
import styles from "./App.module.css";

type PlatformCounts = Record<PlatformType, number>;

interface AppState {
    apps: AppEntity[]
    loading: boolean
    user?: User
    isInitialUserLoaded: boolean
    selectedPlatform: PlatformType
    search: string
    isFormOpen: boolean
    view: View
    isAppAdvertisingShown: boolean
    appToEdit?: AppEntity
}

const EMPTY_COUNTS: PlatformCounts = {
    [PlatformType.ALL]: 0,
    [PlatformType.ANDROID]: 0,
    [PlatformType.WINDOWS]: 0,
    [PlatformType.MACOS]: 0,
    [PlatformType.LINUX]: 0,
};

export default class App extends Component<{}, AppState> {
    unsubscribe$ = new Subject<void>();
    appsSubscription?: Subscription;
    device = devicePlatform();

    state: AppState = {
        apps: [],
        loading: true,
        isInitialUserLoaded: false,
        user: undefined,
        isFormOpen: false,
        view: App.viewFromHash(),
        selectedPlatform: PlatformType.ALL,
        search: "",
        isAppAdvertisingShown: isAndroid,
        appToEdit: undefined
    };

    static viewFromHash(): View {
        return window.location.hash === "#team" ? "team" : "apps";
    }

    onHashChange = () => this.setState({view: App.viewFromHash()});

    navigate = (view: View) => {
        if (view === this.state.view) return;
        const base = window.location.pathname + window.location.search;
        window.history.pushState(null, "", view === "team" ? `${base}#team` : base);
        this.setState({view});
        window.scrollTo({top: 0});
    }

    componentDidMount() {
        window.addEventListener("popstate", this.onHashChange);
        window.addEventListener("hashchange", this.onHashChange);
        temporarySubscription(TokenState.user$, this.unsubscribe$).subscribe(user => {
            this.setState({user, isInitialUserLoaded: true})
            this.loadAppsByRole(user)
        })
    }

    componentWillUnmount() {
        window.removeEventListener("popstate", this.onHashChange);
        window.removeEventListener("hashchange", this.onHashChange);
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    loadAppsByRole = (user: User | undefined) => {
        // The session refreshes every few minutes and re-emits the user; keep one subscription.
        this.appsSubscription?.unsubscribe();
        if (user) {
            const isAdmin = RolesService.isAdmin();
            const source$ = isAdmin ? AppService.apps$ : ReleasesService.releases$;
            this.appsSubscription = temporarySubscription(source$, this.unsubscribe$)
                .subscribe(apps => this.setState({apps, loading: false}));
            if (isAdmin) {
                AppService.loadApps();
            } else {
                ReleasesService.loadReleases();
            }
        }
    }

    countByPlatform = (): PlatformCounts => {
        const counts = {...EMPTY_COUNTS, [PlatformType.ALL]: this.state.apps.length};
        this.state.apps.forEach(app => counts[app.platform] += 1);
        return counts;
    }

    logout = () => {
        this.setState({apps: [], selectedPlatform: PlatformType.ALL, search: "", view: "apps"})
        TokenState.logout()
    }

    onAppDeleteHandler = (app: AppEntity) => {
        AppService.deleteApp(app.id as string)
            .then(() => {
                AppService.loadApps()
                successNotification(`${app.name} ${app.version} was deleted`)
            })
            .catch(error => {
                console.error(error)
                errorNotification(`${app.name} couldn't be deleted. Try again.`)
            })
    }

    onAppReleaseHandler = (app: AppEntity) => {
        if (app.publishedAt) return
        ReleasesService.release(app).then(() => {
            AppService.loadApps()
            successNotification(`${app.name} ${app.version} released`, "Staff can download it now.")
        }).catch(error => {
            console.error(error)
            errorNotification(`${app.name} couldn't be released. Try again.`)
        })
    }

    onFormClose = (closeDialog: boolean = true) => {
        if (closeDialog) {
            this.setState({isFormOpen: false, appToEdit: undefined})
        }
    }

    render() {
        const {apps, user, isInitialUserLoaded, selectedPlatform} = this.state;

        if (!isInitialUserLoaded) {
            return <div className={styles.booting} aria-busy="true" aria-label="Loading App Repo"/>;
        }

        if (!TokenState.isLogged() || !user) {
            return <LoginScreen/>;
        }

        const isAdmin = RolesService.isAdmin();
        const view: View = isAdmin ? this.state.view : "apps";
        const visibleApps = selectedPlatform === PlatformType.ALL
            ? apps
            : apps.filter(app => app.platform === selectedPlatform);

        return (
            <div className={styles.app}>
                <a href={view === "team" ? "#team-title" : "#apps-title"} className={styles.skipLink}>Skip to content</a>

                <TopBar
                    username={user.username}
                    isAdmin={isAdmin}
                    view={view}
                    onNavigate={this.navigate}
                    onUpload={() => this.setState({isFormOpen: true, appToEdit: undefined})}
                    onLogout={this.logout}
                />

                <main className={`${styles.main} ${this.state.isAppAdvertisingShown ? styles.mainWithBanner : ""}`}>
                    {view === "team" ? <TeamPage/> : <AppList
                        apps={visibleApps}
                        counts={this.countByPlatform()}
                        loading={this.state.loading}
                        isAdmin={isAdmin}
                        selectedPlatform={selectedPlatform}
                        devicePlatform={this.device}
                        search={this.state.search}
                        onSearch={search => this.setState({search})}
                        onSelectPlatform={platform => this.setState({selectedPlatform: platform})}
                        onUpload={() => this.setState({isFormOpen: true, appToEdit: undefined})}
                        onEdit={app => this.setState({appToEdit: app, isFormOpen: true})}
                        onRelease={this.onAppReleaseHandler}
                        onDelete={this.onAppDeleteHandler}
                    />}
                </main>

                {isAdmin && (
                    <ApplicationFormDialog
                        key={this.state.appToEdit?.id ?? "new"}
                        type={this.state.appToEdit ? FormType.EDIT : FormType.CREATE}
                        isOpen={this.state.isFormOpen}
                        app={this.state.appToEdit}
                        onClose={this.onFormClose}
                    />
                )}

                {this.state.isAppAdvertisingShown && (
                    <AppAdvertising
                        app={filterStoreMainApp(apps)}
                        onClose={() => this.setState({isAppAdvertisingShown: false})}
                    />
                )}
            </div>
        );
    }
}
